import React from 'react';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import MessageIcon from '@mui/icons-material/Message';

const ConversationListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const ConversationListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;

    &:hover {
      background: #999;
    }
  }
`;

const ConversationItem = styled.div`
  padding: 12px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: ${props => props.active ? '#f0f4ff' : 'transparent'};
  border-left: 3px solid ${props => props.active ? '#667eea' : 'transparent'};

  &:hover {
    background: ${props => props.active ? '#f0f4ff' : '#f8f9fb'};
  }

  &:active {
    background: #f0f4ff;
  }
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const ConversationInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConversationName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LastMessage = styled.div`
  font-size: 11px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: #999;
  white-space: nowrap;
`;

const ConversationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const UnreadBadge = styled.div`
  background: #667eea;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  min-width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
`;

const AnnouncementBadge = styled.span`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  font-size: 9px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  gap: 12px;
  padding: 20px;

  svg {
    font-size: 48px;
    opacity: 0.3;
  }
`;

function ChatConversationList(props) {
  const { conversations, onSelectConversation, isFetching, activeConversationId } = props;

  if (isFetching) {
    return (
      <ConversationListContainer>
        <EmptyState>
          <Spinner animation="border" variant="secondary" size="sm" />
          <span>Loading conversations...</span>
        </EmptyState>
      </ConversationListContainer>
    );
  }

  if (!conversations || conversations.length === 0) {
    return (
      <ConversationListContainer>
        <EmptyState>
          <MessageIcon />
          <span>No conversations yet</span>
          <small>Click "New Message" to start one</small>
        </EmptyState>
      </ConversationListContainer>
    );
  }

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return 'now';
    // Less than 1 hour
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm';
    // Less than 24 hours
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h';
    // Less than 7 days
    if (diff < 604800000) return Math.floor(diff / 86400000) + 'd';
    // Otherwise show date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <ConversationListContainer>
      <ConversationListScroll>
        {conversations.map((conversation) => {
          const initials = conversation.participantName 
            ? conversation.participantName.charAt(0).toUpperCase()
            : '?';
          
          return (
            <ConversationItem 
              key={conversation.id}
              active={activeConversationId === conversation.id}
              onClick={() => onSelectConversation(conversation.id)}
            >
              <AvatarWrapper title={conversation.participantName}>
                {conversation.is_group_chat ? <GroupIcon fontSize="small" /> : initials}
              </AvatarWrapper>
              
              <ConversationInfo>
                <ConversationRow>
                  <ConversationName>
                    {conversation.isAnnouncementChannel && <AnnouncementBadge>📢 ANNOUNCEMENTS</AnnouncementBadge>}
                    {conversation.is_group_chat && !conversation.isAnnouncementChannel && <GroupIcon style={{ fontSize: '12px' }} />}
                    {!conversation.isAnnouncementChannel && conversation.participantName}
                  </ConversationName>
                  <MessageTime>{formatTime(conversation.last_message_time)}</MessageTime>
                </ConversationRow>
                <LastMessage title={conversation.lastMessage}>
                  {conversation.lastMessage}
                </LastMessage>
              </ConversationInfo>

              {conversation.unread_count > 0 && (
                <UnreadBadge>{conversation.unread_count > 99 ? '99+' : conversation.unread_count}</UnreadBadge>
              )}
            </ConversationItem>
          );
        })}
      </ConversationListScroll>
    </ConversationListContainer>
  );
}

// Custom comparison for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.conversations === nextProps.conversations &&
    prevProps.activeConversationId === nextProps.activeConversationId &&
    prevProps.isFetching === nextProps.isFetching &&
    prevProps.onSelectConversation === nextProps.onSelectConversation
  );
};

export default React.memo(ChatConversationList, arePropsEqual);
